import React, { memo, useMemo } from "react";
import {
  AiCommentType,
  AudioPlayerType,
  ButtonLinkType,
  celltype,
  ConceptItemType,
  ContentType,
  ElementType,
  HelpType,
  ImageType,
  MultipleSelectionType,
  PercentageType,
  PieChartType,
  PresetType,
  PushNotificationType,
  ReferenceType,
  SelfCheckType,
  SingleSelectionType,
  TextareaType,
  ThoughtmapType,
  TypographyFromDashboardType,
  TypographyFromDataType,
  TypographyType,
} from "data/CellComponent";
import { ProgramContentType } from "data/BlockComponent";
import { useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";

import {
  getAutomaticThoughtId,
  getPercentageSum,
  getTaskIdFromTaskKey,
} from "logic/logics";

import {
  AddActivityPlanCell,
  AiCommentCell,
  ButtonGroupCell,
  ButtonGroupMultipleChoiceCell,
  ConceptItemCell,
  DateSelectCell,
  ExtractFromThoughtRecordCell,
  FeedbackCell,
  ImageCell,
  PercentageCell,
  PieChartCell,
  PresetCell,
  ReferenceCell,
  SelectCell,
  SelectIntermediateBeliefCell,
  SelfCheckRadioButtonCell,
  TextareaCell,
  ThoughtmapCell,
  TimeSelectCell,
  TypographyCell,
  TypographyFromConceptNoteCell,
  TypographyFromDashboardCell,
  TypographyFromDataCell,
} from "components/task/content/cell";
import RequestPushNotificationButton from "components/common/RequestPushNotificationButton";
import { useTranslation } from "react-i18next";

import RequestStoreReviewButton from "components/common/RequestStoreReviewButton";
import { automaticThoughtIdType } from "api/conceptApi";

import ButtonLink from "components/common/ButtonLink";
import CellContainer from "./CellContainer";
import { isTaskIdValid } from "data/programData";
import ValueCompass from "components/value/ValueCompass";
import HelpCell from "./HelpCell";
import AudioPlayerCell from "./AudioPlayerCell";
import dayjs from "dayjs";
import type { TaskContentViewerMode } from "../TaskContent";

function Cell({
  type,
  contentStr,
  blockIndex,
  lineIndex,
  cellIndex,
  taskKey,
  thoughtRecordKey,
  meditationRecordKey,
  activityRecordKey,
  data,
  setData,
  translationVersion,
  readonly,
  isShown,
  viewerMode = "auto",
  onAttemptEdit,
}: {
  type: celltype;
  contentStr: string;
  blockIndex: number;
  lineIndex: number;
  cellIndex: number;
  taskKey: string;
  thoughtRecordKey?: string;
  meditationRecordKey?: string;
  activityRecordKey?: string;
  data?: ProgramContentType[];
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  translationVersion?: string;
  readonly: boolean;
  isShown: boolean;
  viewerMode?: TaskContentViewerMode;
  onAttemptEdit?: () => void;
}) {
  const content: ContentType = useMemo(
    () => JSON.parse(contentStr),
    [contentStr]
  );

  const translationKey = (content as TypographyType).translationKey;
  const user = useRecoilValue(userAtom);
  const isCoach =
    viewerMode === "auto"
      ? (!!user?.accessToken && user?.role !== "patient") || false
      : viewerMode === "coach";

  const { t } = useTranslation("translation", {
    keyPrefix: `task.activity.${getTaskIdFromTaskKey(
      taskKey
    )}.${translationVersion}`,
  });

  const { t: t_common } = useTranslation("translation", {
    keyPrefix: "task.activity.common",
  });

  const taskId_fromOtherTask = translationKey?.split(":")[0];
  const translationVersion_fromOtherTask = translationKey?.split(":")[1];
  const translationKey_fromOtherTask =
    translationKey?.split(":")[2] || translationVersion_fromOtherTask;

  const { t: t_fromOtherTask } = useTranslation("translation", {
    keyPrefix: `task.activity.${taskId_fromOtherTask}.${
      translationVersion_fromOtherTask?.startsWith("v")
        ? translationVersion_fromOtherTask
        : "v1"
    }`,
  });

  const translatedText_noCommon = isTaskIdValid(taskId_fromOtherTask || "")
    ? t_fromOtherTask(translationKey_fromOtherTask || "")
    : t(translationKey || "");

  const translatedText = !translationVersion
    ? translationKey || ""
    : translationKey?.startsWith("common")
      ? t_common(translationKey?.split(":")[1] || "")
      : translationKey === "$A$"
        ? t_common(translationKey || "")
        : (content as TypographyType)?.isLabel
          ? translationKey || ""
          : translatedText_noCommon;

  const translatedOptions =
    (content as SingleSelectionType)["options"]?.map(
      (each) =>
        each.text ||
        (each.translationKey?.startsWith("common")
          ? t_common(each.translationKey?.split(":")[1] || "")
          : t(each.translationKey || ""))
    ) || [];

  const translationKeys =
    (content as SingleSelectionType)["options"]
      ?.filter((element) => t(element.translationKey || ""))
      .map((each) => each.translationKey || "") || [];

  const isCoachField = content.coach;
  return (
    <CellContainer
      readOnly={
        readonly ||
        (isCoachField && !isCoach) ||
        (type === "buttongroup" &&
          !isCoachField &&
          isCoach &&
          (!translatedOptions.includes("네") ||
            !translatedOptions.includes("아니요")))
      }
      isCoachFieldEmpty={
        isCoach &&
        !content.optional &&
        isCoachField &&
        (content.value || "").trim().length === 0
      }
      onAttemptEdit={onAttemptEdit}
    >
      {type === "typography" ? (
        <TypographyCell
          {...content}
          text={
            (content as TypographyType)?.translationKey
              ? translatedText
              : (content as TypographyType)?.text ||
                (content as TypographyType)?.value ||
                ""
          }
        />
      ) : type === "dateHeader" ? (
        <TypographyCell
          {...content}
          text={`${dayjs(
            (content as TypographyType)?.translationKey
              ? translatedText
              : (content as TypographyType)?.text ||
                  (content as TypographyType)?.value ||
                  ""
          ).format(t_common("MMM D일 ddd요일"))}`}
          isHeader={true}
        />
      ) : type === "image" ? (
        <ImageCell taskKey={taskKey} url={(content as ImageType).imageUrl} />
      ) : type === "audioPlayer" ? (
        <AudioPlayerCell
          key={`${taskKey || "noTaskKey"}_${meditationRecordKey || "noMrk"}_${(content as AudioPlayerType).audioUrl || "noAudioUrl"}`}
          taskKey={taskKey}
          meditationRecordKey={meditationRecordKey}
          content={content}
          blockIndex={blockIndex}
          lineIndex={lineIndex}
          cellIndex={cellIndex}
          setData={setData}
          readonly={readonly}
        />
      ) : type === "select" ? (
        <SelectCell
          defaultValue={(content as SingleSelectionType).value}
          selectedIndex={(content as SingleSelectionType).selectedIndex}
          blockIndex={blockIndex}
          options={translatedOptions}
          setData={setData}
          lineIndex={lineIndex}
          cellIndex={cellIndex}
          isCoachField={(content as ElementType).coach}
          isCoach={isCoach}
        />
      ) : type === "buttongroup" ? (
        <ButtonGroupCell
          defaultValue={(content as SingleSelectionType).value}
          selectedIndex={(content as SingleSelectionType).selectedIndex}
          blockIndex={blockIndex}
          options={translatedOptions}
          setData={setData}
          lineIndex={lineIndex}
          cellIndex={cellIndex}
          isCoach={isCoach}
        />
      ) : type === "buttongroup_multipleChoice" ? (
        <ButtonGroupMultipleChoiceCell
          initialSelectedIndices={
            (content as MultipleSelectionType).selectedIndices
          }
          requiredSelectionCount={
            (content as MultipleSelectionType).requiredSelectionCount
          }
          blockIndex={blockIndex}
          options={translatedOptions}
          {...(translationKeys.every((element) => element.length > 0) && {
            optionKeys: translationKeys,
          })}
          setData={setData}
          lineIndex={lineIndex}
          cellIndex={cellIndex}
          isCoach={isCoach}
        />
      ) : type === "textarea" ? (
        <TextareaCell
          readOnly={(content as TextareaType).readonly}
          placeholder={(content as TextareaType).placeholder}
          initialHtmlString={(content as TextareaType).htmlString}
          initialValue={(content as TextareaType).value}
          isCoachField={(content as TextareaType).coach}
          isCoach={isCoach}
          setData={setData}
          lineIndex={lineIndex}
          cellIndex={cellIndex}
          blockIndex={blockIndex}
          editorKey={(content as TextareaType)?.editorKey}
          id={(content as TextareaType)?.id}
        />
      ) : type === "preset" ? (
        <PresetCell
          taskKey={taskKey || ""}
          readOnly={(content as TextareaType).coach && !isCoach}
          placeholder={(content as TextareaType).placeholder}
          initialHtmlString={(content as TextareaType).htmlString}
          initialValue={(content as TextareaType).value || undefined}
          initialSelectedIndex={(content as SingleSelectionType).selectedIndex}
          isCoachField={(content as TextareaType).coach}
          isCoach={isCoach}
          setData={setData}
          options={translatedOptions}
          lineIndex={lineIndex}
          cellIndex={cellIndex}
          blockIndex={blockIndex}
          editorKey={(content as TextareaType)?.editorKey}
          presetKey={(content as PresetType)?.presetKey}
        />
      ) : type === "add_activity_plan" ? (
        <AddActivityPlanCell
          taskKey={taskKey || ""}
          ark={activityRecordKey}
          placeholder={(content as TextareaType).placeholder}
          initialValue={(content as TextareaType).value || undefined}
          isCoach={isCoach}
          setData={setData}
          lineIndex={lineIndex}
          cellIndex={cellIndex}
          blockIndex={blockIndex}
          editorKey={(content as TextareaType)?.editorKey}
        />
      ) : type === "reference" ? (
        <ReferenceCell
          taskKey={taskKey || ""}
          placeholder={(content as TextareaType).placeholder}
          initialHtmlString={(content as TextareaType).htmlString}
          initialValue={(content as TextareaType).value}
          isCoach={isCoach}
          setData={setData}
          lineIndex={lineIndex}
          cellIndex={cellIndex}
          blockIndex={blockIndex}
          editorKey={(content as TextareaType)?.editorKey}
          referenceKey={(content as ReferenceType)?.referenceKey}
        />
      ) : type === "aiComment" ? (
        <AiCommentCell
          taskKey={taskKey || ""}
          thoughtRecordKey={thoughtRecordKey}
          initialValue={(content as AiCommentType).value}
          aiCommentValue={(content as AiCommentType).aiCommentValue}
          isCoach={isCoach}
          setData={setData}
          lineIndex={lineIndex}
          cellIndex={cellIndex}
          blockIndex={blockIndex}
          editorKey={(content as AiCommentType)?.editorKey}
          aiCommentKey={(content as AiCommentType)?.aiCommentKey}
        />
      ) : type === "typographyFromDashboard" ? (
        <TypographyFromDashboardCell
          taskKey={taskKey}
          initialValue={content.value}
          setData={setData}
          lineIndex={lineIndex}
          cellIndex={cellIndex}
          blockIndex={blockIndex}
          id1={(content as TypographyFromDashboardType).id1}
          id2={(content as TypographyFromDashboardType).id2}
          level={(content as TypographyFromDashboardType).level}
        />
      ) : type === "typographyFromConceptNote" ? (
        <TypographyFromConceptNoteCell
          taskKey={taskKey || ""}
          initialHtmlString={(content as TextareaType).htmlString}
          initialValue={(content as TextareaType).value}
          isCoach={isCoach}
          setData={setData}
          lineIndex={lineIndex}
          cellIndex={cellIndex}
          blockIndex={blockIndex}
          editorKey={(content as TextareaType)?.editorKey}
          id1={(content as TypographyFromDashboardType).id1}
          id2={(content as TypographyFromDashboardType).id2}
        />
      ) : type === "conceptItem" ? (
        <ConceptItemCell
          taskKey={taskKey || ""}
          conceptKey={(content as ConceptItemType).conceptKey}
          isNotConfirmed={(content as ConceptItemType).isNotConfirmed}
        />
      ) : type === "selfCheck" ? (
        <SelfCheckRadioButtonCell
          defaultValue={(content as SelfCheckType).selectedScore}
          blockIndex={blockIndex}
          evaluation={Object.fromEntries(
            Object.entries((content as SelfCheckType).evaluation).map(
              ([key, value]) => [t_common(key), value]
            )
          )}
          setData={setData}
          lineIndex={lineIndex}
          cellIndex={cellIndex}
        />
      ) : type === "extractFromThoughtRecord" ? (
        <ExtractFromThoughtRecordCell
          automaticThoughtId={
            getAutomaticThoughtId(
              getTaskIdFromTaskKey(taskKey)
            ) as automaticThoughtIdType
          }
          isPositive={taskKey.startsWith("7-4-A")}
          defaultValue={content.value}
          blockIndex={blockIndex}
          data={data || []}
          setData={setData}
          lineIndex={lineIndex}
          cellIndex={cellIndex}
        />
      ) : type === "pushNotification" ? (
        <RequestPushNotificationButton
          setData={setData}
          blockIndex={blockIndex}
          lineIndex={lineIndex}
          cellIndex={cellIndex}
          showFromIdIfDenied={
            (content as PushNotificationType).showFromIdIfDenied
          }
          disabled={readonly}
          clicked={(content as PushNotificationType).clicked}
        />
      ) : type === "storeReview" ? (
        <RequestStoreReviewButton disabled={readonly} />
      ) : type === "buttonLink" ? (
        <ButtonLink disabled={readonly} {...(content as ButtonLinkType)} />
      ) : type === "date" ? (
        <DateSelectCell
          editorKey={(content as ElementType)?.editorKey}
          defaultValue={(content as ElementType).value}
          blockIndex={blockIndex}
          setData={setData}
          lineIndex={lineIndex}
          cellIndex={cellIndex}
        />
      ) : type === "time" ? (
        <TimeSelectCell
          key={`${taskKey || "noTaskKey"}_${thoughtRecordKey || "noTrk"}_${meditationRecordKey || "noMrk"}`}
          editorKey={(content as ElementType)?.editorKey}
          defaultValue={(content as ElementType).value}
          blockIndex={blockIndex}
          setData={setData}
          lineIndex={lineIndex}
          cellIndex={cellIndex}
          timeStepOption={content?.timeSelectStep || "hour"}
        />
      ) : type === "percentage" ? (
        <PercentageCell
          id={(content as PercentageType).id}
          initialPercentage={
            content?.complementPercentageIdList
              ? Math.max(
                  0,
                  100 -
                    getPercentageSum(
                      data || [],
                      content?.complementPercentageIdList
                    )
                )
              : (content as PercentageType).percentage
          }
          placeholder={(content as PercentageType).placeholder}
          min={(content as PercentageType).min}
          max={(content as PercentageType).max}
          step={(content as PercentageType).step}
          warnIfZero={(content as PercentageType).warnIfZero}
          inputKey={`${(content as PercentageType).editorKey}`}
          setData={setData}
          lineIndex={lineIndex}
          cellIndex={cellIndex}
          blockIndex={blockIndex}
          readOnly={!!content?.complementPercentageIdList}
        />
      ) : type === "typographyFromData" ? (
        <TypographyFromDataCell
          taskKey={taskKey}
          {...content}
          data={data || []}
          setData={setData}
          lineIndex={lineIndex}
          cellIndex={cellIndex}
          blockIndex={blockIndex}
          dataMapping={(content as TypographyFromDataType).dataMapping || {}}
          initialValue={(content as TypographyFromDataType).value}
          translationVersion={translationVersion}
          text={
            (content as TypographyType)?.translationKey
              ? translatedText
              : (content as TypographyType)?.text || ""
          }
        />
      ) : type === "pieChart" ? (
        <PieChartCell
          data={data || []}
          setData={setData}
          lineIndex={lineIndex}
          cellIndex={cellIndex}
          blockIndex={blockIndex}
          pieChartId={(content as PieChartType).pieChartId}
        />
      ) : type === "thoughtmap" ? (
        <ThoughtmapCell
          from={(content as ThoughtmapType).from}
          to={(content as ThoughtmapType).to}
          fixedValue={(content as ThoughtmapType).fixedValue}
        />
      ) : type === "selectIntermediateBelief" ? (
        <SelectIntermediateBeliefCell
          selectedIndex={(content as SingleSelectionType).selectedIndex}
          blockIndex={blockIndex}
          setData={setData}
          lineIndex={lineIndex}
          cellIndex={cellIndex}
          readOnly={readonly}
          onAttemptEdit={onAttemptEdit}
        />
      ) : type === "valueCompass" ? (
        <ValueCompass excludePatterns />
      ) : type === "feedback" ? (
        <FeedbackCell
          taskKey={taskKey || ""}
          savedFeedback={(content as TextareaType).value}
          setData={setData}
          lineIndex={lineIndex}
          cellIndex={cellIndex}
          blockIndex={blockIndex}
          editorKey={(content as TextareaType)?.editorKey}
        />
      ) : type === "help" ? (
        <HelpCell
          key={`${thoughtRecordKey}_${(content as HelpType)?.translationKey}`}
          label={(content as HelpType).label || "도움말"}
          helpText={
            (content as HelpType)?.translationKey
              ? translatedText
              : (content as HelpType)?.value || ""
          }
        />
      ) : (
        <div></div>
      )}
    </CellContainer>
  );
}

export default memo(Cell);
